import type { CaptureSource } from '@cerebro-diem/core';

export const LEGACY_CAPTURE_KEY = 'cerebro.pending-captures';
export const CAPTURE_DB = 'cerebro-captures';
export const CAPTURE_STORE = 'captures';
export const RAW_RECORDING_STORE = 'recordings';
export const RAW_CHUNKS_STORE = 'chunks';
const messages = {
  unavailable: 'Browser storage is unavailable. It could not complete this operation.',
  quota: 'Browser storage is full. It could not complete this operation.',
  unreadable: 'Stored captures could not be read. They remain untouched.',
  blocked: 'Browser storage is blocked by another open page. Close that page and try again.',
  write_failed: 'Browser storage could not complete this operation.',
  read_failed: 'Browser storage could not be read. Existing captures remain untouched.',
  sending: 'This capture is being sent. Its local copy cannot be discarded yet.',
  confirmed: 'This capture is already saved to Cerebro. Use Send now to finish local cleanup.',
  owner_changed: 'The signed-in account changed. The stored capture remains untouched.',
  migration_changed: 'The old capture queue changed during migration. It remains untouched; try again.',
};
export class CaptureStorageError extends Error {
  constructor(readonly category: keyof typeof messages) { super(messages[category]); this.name = 'CaptureStorageError'; }
}
function failure(error: unknown, operation: 'read_failed' | 'write_failed') {
  if (error instanceof CaptureStorageError) return error;
  const name = error instanceof DOMException ? error.name : '';
  return new CaptureStorageError(name === 'QuotaExceededError' ? 'quota' : name === 'SecurityError' || name === 'NotAllowedError' ? 'unavailable' : operation);
}

/** The parser is the stored schema; consumers infer their types from its result. */
export function parseCapture(value: unknown) {
  if (!value || typeof value !== 'object') throw new CaptureStorageError('unreadable');
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string' || !v.id || typeof v.userId !== 'string' || !v.userId || typeof v.text !== 'string' ||
    (v.source !== 'text' && v.source !== 'voice') || typeof v.createdAt !== 'string' || !Number.isFinite(Date.parse(v.createdAt)) ||
    (v.attempts !== undefined && (!Number.isSafeInteger(v.attempts) || (v.attempts as number) < 0)) ||
    (v.lastError !== undefined && v.lastError !== null && typeof v.lastError !== 'string') ||
    (v.transcript !== undefined && (typeof v.transcript !== 'string' || !v.transcript.trim())) ||
    (v.confirmed !== undefined && typeof v.confirmed !== 'boolean')) throw new CaptureStorageError('unreadable');
  let recording: { blob: Blob; durationMs: number } | undefined;
  if (v.recording !== undefined) {
    const audio = v.recording as Record<string, unknown> | null;
    if (v.source !== 'voice' || !audio || !(audio.blob instanceof Blob) || audio.blob.size === 0 ||
      !/^audio\/(webm|mp4)(;|$)/.test(audio.blob.type) || typeof audio.durationMs !== 'number' || !Number.isFinite(audio.durationMs) || audio.durationMs < 0) {
      throw new CaptureStorageError('unreadable');
    }
    recording = { blob: audio.blob, durationMs: audio.durationMs };
  }
  if (!v.text.trim() && !recording) throw new CaptureStorageError('unreadable');
  return {
    id: v.id, userId: v.userId, source: v.source as CaptureSource, text: v.text, createdAt: v.createdAt,
    attempts: (v.attempts as number | undefined) ?? 0, lastError: (v.lastError as string | null | undefined) ?? null,
    recording, transcript: v.transcript as string | undefined, confirmed: (v.confirmed as boolean | undefined) ?? false,
  };
}
export type PendingCapture = ReturnType<typeof parseCapture>;
export type CaptureRecording = NonNullable<PendingCapture['recording']>;
function parseList(value: unknown): PendingCapture[] {
  if (!Array.isArray(value)) throw new CaptureStorageError('unreadable');
  const captures = value.map(parseCapture);
  if (new Set(captures.map(c => c.id)).size !== captures.length) throw new CaptureStorageError('unreadable');
  return captures;
}

export function openCaptureDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    try {
      const request = indexedDB.open(CAPTURE_DB, 2);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(CAPTURE_STORE)) request.result.createObjectStore(CAPTURE_STORE, { keyPath: 'id' });
        request.result.createObjectStore(RAW_RECORDING_STORE, { keyPath: 'id' });
        request.result.createObjectStore(RAW_CHUNKS_STORE, { keyPath: ['recordingId', 'index'] });
      };
      request.onerror = () => { settled = true; reject(failure(request.error, 'read_failed')); };
      request.onblocked = () => { settled = true; reject(new CaptureStorageError('blocked')); };
      request.onsuccess = () => {
        if (settled) { request.result.close(); return; }
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
    } catch (error) { reject(failure(error, 'read_failed')); }
  });
}

/** Validate and mutate inside one transaction; concurrent tabs never replace an entire queue. */
async function transaction(mode: IDBTransactionMode, change?: (store: IDBObjectStore, captures: PendingCapture[]) => void): Promise<PendingCapture[]> {
  const db = await openCaptureDatabase();
  try {
    return await new Promise<PendingCapture[]>((resolve, reject) => {
      let reason: unknown;
      let captures: PendingCapture[] = [];
      let tx: IDBTransaction;
      try { tx = db.transaction(CAPTURE_STORE, mode, { durability: 'strict' }); }
      catch (error) { reject(failure(error, mode === 'readonly' ? 'read_failed' : 'write_failed')); return; }
      tx.oncomplete = () => resolve(captures);
      tx.onabort = () => reject(failure(reason ?? tx.error, mode === 'readonly' ? 'read_failed' : 'write_failed'));
      tx.onerror = () => { reason ??= tx.error; };
      const store = tx.objectStore(CAPTURE_STORE);
      let request: IDBRequest<unknown[]> | undefined;
      try { request = store.getAll(); } catch (error) { reason = error; tx.abort(); }
      if (!request) return;
      request.onsuccess = () => {
        try {
          captures = parseList(request.result);
          if (change) {
            change(store, captures);
            const after = store.getAll();
            after.onsuccess = () => {
              try { captures = parseList(after.result); } catch (error) { reason = error; tx.abort(); }
            };
          }
        }
        catch (error) { reason = error; tx.abort(); }
      };
    });
  } finally { db.close(); }
}

async function migrateLegacy(): Promise<void> {
  let raw: string | null;
  try { raw = localStorage.getItem(LEGACY_CAPTURE_KEY); }
  catch (error) { throw failure(error, 'read_failed'); }
  if (raw === null) return;
  let legacy: PendingCapture[];
  try { legacy = parseList(JSON.parse(raw)); }
  catch { throw new CaptureStorageError('unreadable'); }
  await transaction('readwrite', (store, current) => {
    for (const capture of legacy) {
      const existing = current.find(c => c.id === capture.id);
      if (existing) {
        if (existing.userId !== capture.userId || existing.text !== capture.text || existing.source !== capture.source || existing.createdAt !== capture.createdAt) {
          throw new CaptureStorageError('unreadable');
        }
      } else store.add(capture);
    }
  });
  const verified = await transaction('readonly');
  for (const capture of legacy) {
    const stored = verified.find(c => c.id === capture.id);
    if (!stored || stored.userId !== capture.userId || stored.text !== capture.text || stored.source !== capture.source || stored.createdAt !== capture.createdAt) {
      throw new CaptureStorageError('unreadable');
    }
  }
  // Only retire the exact source that committed. Changed or unreadable data is never erased.
  try {
    const remaining = localStorage.getItem(LEGACY_CAPTURE_KEY);
    if (remaining !== null && remaining !== raw) throw new CaptureStorageError('migration_changed');
    if (remaining !== null) localStorage.removeItem(LEGACY_CAPTURE_KEY);
  } catch (error) { throw failure(error, 'write_failed'); }
}
export async function loadCaptures(): Promise<PendingCapture[]> {
  await migrateLegacy();
  return transaction('readonly');
}
export async function addCapture(capture: PendingCapture): Promise<PendingCapture[]> {
  const valid = parseCapture(capture);
  await migrateLegacy();
  return transaction('readwrite', (store, captures) => {
    const existing = captures.find(c => c.id === valid.id);
    if (existing) {
      if (existing.userId !== valid.userId || existing.text !== valid.text || existing.source !== valid.source || existing.createdAt !== valid.createdAt || existing.recording || valid.recording) throw new CaptureStorageError('unreadable');
      return;
    }
    store.add(valid);
  });
}
export async function updateCapture(id: string, ownerId: string, patch: Partial<Pick<PendingCapture, 'attempts' | 'lastError' | 'transcript' | 'confirmed'>>): Promise<PendingCapture[]> {
  return transaction('readwrite', (store, captures) => {
    const capture = captures.find(c => c.id === id);
    if (!capture) throw new CaptureStorageError('unreadable');
    if (capture.userId !== ownerId) throw new CaptureStorageError('owner_changed');
    store.put(parseCapture({ ...capture, ...patch }));
  });
}
export async function removeCapture(id: string, ownerId: string): Promise<PendingCapture[]> {
  return transaction('readwrite', (store, captures) => {
    const capture = captures.find(c => c.id === id);
    if (!capture || capture.userId !== ownerId) throw new CaptureStorageError('owner_changed');
    store.delete(id);
  });
}
